import React, { useState, useEffect, useCallback } from 'react';
import { proFetch, estPatron } from '../../utils/proApi';
import { Page, EnTetePage, Etiquette, Bouton, Mention, Toast } from '../../components/pro/ui';

// Comptes de l'espace Pro. Réservé à la direction.
//
// Deux rôles : « patron » touche à l'argent (factures) et aux prix, « conducteur »
// mène les chantiers. Le serveur tranche pour de bon — cet écran ne fait
// qu'éviter d'afficher des boutons qui ne marcheraient pas.

const ROLES = {
  patron: { label: 'Direction', detail: 'Factures, prix, comptes', classe: 'border-orange-200 text-orange-700' },
  conducteur: { label: 'Conduite de travaux', detail: 'Chantiers, relevés, planning', classe: 'border-slate-300 text-slate-600' },
};
const nomDe = (c) => [c?.prenom, c?.nom].filter(Boolean).join(' ');
const jour = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

function FormulaireEdition({ compte, brouillon, setBrouillon, onValider, onAnnuler, moi }) {
  return (
    <div className="bg-orange-50/60 border border-orange-200 rounded-xl p-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <label className="block">
          <Etiquette className="mb-1">Identifiant</Etiquette>
          <input value={brouillon.identifiant} disabled={moi}
                 onChange={e => setBrouillon(b => ({ ...b, identifiant: e.target.value }))}
                 className="champ w-full disabled:bg-slate-100 disabled:text-slate-400" />
          {moi && <span className="block text-[11px] text-slate-400 mt-1">Votre propre identifiant ne se change pas d'ici.</span>}
        </label>
        <label className="block">
          <Etiquette className="mb-1">Prénom</Etiquette>
          <input value={brouillon.prenom} onChange={e => setBrouillon(b => ({ ...b, prenom: e.target.value }))}
                 placeholder="Marius" className="champ w-full" />
        </label>
        <label className="block">
          <Etiquette className="mb-1">Nom</Etiquette>
          <input value={brouillon.nom} onChange={e => setBrouillon(b => ({ ...b, nom: e.target.value }))}
                 placeholder="Caprariu" className="champ w-full" />
        </label>
        <label className="block">
          <Etiquette className="mb-1">E-mail</Etiquette>
          <input type="email" value={brouillon.email} onChange={e => setBrouillon(b => ({ ...b, email: e.target.value }))}
                 placeholder="marius@apexrenovation.fr" className="champ w-full" />
        </label>
        <label className="block">
          <Etiquette className="mb-1">Téléphone</Etiquette>
          <input type="tel" value={brouillon.telephone} onChange={e => setBrouillon(b => ({ ...b, telephone: e.target.value }))}
                 placeholder="06 12 34 56 78" className="champ w-full" />
        </label>
        <label className="block">
          <Etiquette className="mb-1">Rôle</Etiquette>
          <select value={brouillon.role} onChange={e => setBrouillon(b => ({ ...b, role: e.target.value }))} className="champ w-full">
            {Object.entries(ROLES).map(([id, r]) => <option key={id} value={id}>{r.label}</option>)}
          </select>
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-3">
        <Bouton variante="primaire" onClick={onValider}>Enregistrer</Bouton>
        <Bouton variante="neutre" onClick={onAnnuler}>Annuler</Bouton>
        <span className="text-xs text-slate-400">{ROLES[brouillon.role].detail}</span>
      </div>
    </div>
  );
}

export default function ProComptes() {
  const [comptes, setComptes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [creation, setCreation] = useState(false);
  const [form, setForm] = useState({ identifiant: '', prenom: '', nom: '', email: '', telephone: '', role: 'conducteur' });
  const [message, setMessage] = useState(null);
  const [edition, setEdition] = useState(null);   // identifiant en cours d'édition
  const [lien, setLien] = useState(null);         // dernier lien émis, à recopier au besoin
  const [brouillon, setBrouillon] = useState({ identifiant: '', prenom: '', nom: '', email: '', telephone: '', role: 'conducteur' });

  const moi = (localStorage.getItem('pro_user') || '').toLowerCase();

  const toast = (texte, type = 'ok') => { setMessage({ texte, type }); setTimeout(() => setMessage(null), 4000); };

  const charger = useCallback(async () => {
    try {
      const r = await proFetch('/comptes');
      if (!r.ok) throw new Error((await r.json()).error || 'Accès refusé');
      setComptes(await r.json());
    } catch (e) { toast(e.message, 'err'); }
    finally { setChargement(false); }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const creer = async () => {
    try {
      const r = await proFetch('/comptes', { method: 'POST', body: JSON.stringify(form) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Erreur');
      setLien({ identifiant: d.identifiant, lien: d.lien, envoye: d.envoye, motif: d.motif });
      toast(d.envoye ? `Compte ${d.identifiant} créé, invitation envoyée` : `Compte ${d.identifiant} créé — ${d.motif || 'invitation non envoyée'}`,
            d.envoye ? 'ok' : 'err');
      setForm({ identifiant: '', prenom: '', nom: '', email: '', telephone: '', role: 'conducteur' });
      setCreation(false);
      charger();
    } catch (e) { toast(e.message, 'err'); }
  };

  const modifier = async (identifiant, champs, libelle) => {
    try {
      const r = await proFetch(`/comptes/${identifiant}`, { method: 'PATCH', body: JSON.stringify(champs) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Erreur');
      toast(libelle);
      charger();
    } catch (e) { toast(e.message, 'err'); }
  };

  const envoyerLien = async (c) => {
    try {
      const r = await proFetch(`/comptes/${c.identifiant}/invitation`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Erreur');
      setLien({ identifiant: c.identifiant, ...d });
      toast(d.envoye ? `Lien envoyé à ${c.email}` : `Lien créé — ${d.motif}`, d.envoye ? 'ok' : 'err');
      charger();
    } catch (e) { toast(e.message, 'err'); }
  };

  const ouvrirEdition = (c) => {
    setEdition(c.identifiant);
    setBrouillon({ identifiant: c.identifiant, prenom: c.prenom || '', nom: c.nom || '',
                   email: c.email || '', telephone: c.telephone || '', role: c.role });
  };

  // Tout part en une fois : renommer quelqu'un et lui changer son rôle ne
  // devrait pas demander deux allers-retours.
  const enregistrer = async (c) => {
    const champs = {};
    if (brouillon.identifiant.trim() && brouillon.identifiant.trim() !== c.identifiant) champs.identifiant = brouillon.identifiant.trim();
    for (const k of ['prenom', 'nom', 'email', 'telephone']) {
      if (brouillon[k] !== (c[k] || '')) champs[k] = brouillon[k];
    }
    if (brouillon.role !== c.role) champs.role = brouillon.role;
    if (!Object.keys(champs).length) { setEdition(null); return; }
    await modifier(c.identifiant, champs, `${champs.identifiant || c.identifiant} mis à jour`);
    setEdition(null);
  };

  if (!estPatron()) {
    return <div className="px-4 py-10 text-center text-slate-400">Cet écran est réservé à la direction.</div>;
  }
  if (chargement) return <div className="px-4 py-8 text-slate-400">Chargement…</div>;

  return (
    <Page>
      <EnTetePage titre="Comptes"
                  sous="Qui accède à l'espace Pro, et jusqu'où. Seule la direction émet les factures et modifie les prix.">
        <Bouton variante={creation ? 'neutre' : 'sombre'} icone={creation ? undefined : 'plus'}
                onClick={() => setCreation(v => !v)}>
          {creation ? 'Annuler' : 'Nouveau compte'}
        </Bouton>
      </EnTetePage>

      {lien?.lien && (
        <div className="bg-white border border-orange-200 rounded-xl p-4 mb-5">
          <p className="text-sm font-semibold text-slate-800">
            Lien d'activation pour {lien.identifiant}
            {lien.envoye ? <span className="font-normal text-slate-400"> — envoyé par email</span>
                         : <span className="font-normal text-amber-700"> — non envoyé : {lien.motif}</span>}
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <code className="flex-1 min-w-0 truncate text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-600">
              {lien.lien}
            </code>
            <Bouton variante="neutre" onClick={() => {
              navigator.clipboard?.writeText(lien.lien).then(() => toast('Lien copié'), () => toast('Copie impossible', 'err'));
            }}>Copier</Bouton>
            <Bouton variante="discret" onClick={() => setLien(null)}>Masquer</Bouton>
          </div>
          <p className="text-xs text-slate-400 mt-2">
            Valable 7 jours, un seul usage. En émettre un nouveau annule celui-ci.
          </p>
        </div>
      )}

      {creation && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 mb-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <label className="block">
              <Etiquette className="mb-1">Identifiant</Etiquette>
              <input value={form.identifiant} onChange={e => setForm(f => ({ ...f, identifiant: e.target.value }))}
                     placeholder="prenom" className="champ w-full" />
            </label>
            <label className="block">
              <Etiquette className="mb-1">Prénom</Etiquette>
              <input value={form.prenom} onChange={e => setForm(f => ({ ...f, prenom: e.target.value }))}
                     placeholder="Marius" className="champ w-full" />
            </label>
            <label className="block">
              <Etiquette className="mb-1">Nom</Etiquette>
              <input value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
                     placeholder="Caprariu" className="champ w-full" />
            </label>
            <label className="block">
              <Etiquette className="mb-1">E-mail</Etiquette>
              <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                     placeholder="marius@apexrenovation.fr" className="champ w-full" />
            </label>
            <label className="block">
              <Etiquette className="mb-1">Téléphone</Etiquette>
              <input type="tel" value={form.telephone} onChange={e => setForm(f => ({ ...f, telephone: e.target.value }))}
                     placeholder="06 12 34 56 78" className="champ w-full" />
            </label>
            <label className="block">
              <Etiquette className="mb-1">Rôle</Etiquette>
              <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} className="champ w-full">
                {Object.entries(ROLES).map(([id, r]) => <option key={id} value={id}>{r.label}</option>)}
              </select>
            </label>

          </div>
          <div className="flex items-center gap-3 mt-3">
            <Bouton variante="primaire" onClick={creer} disabled={!form.identifiant.trim()}>
              Créer et envoyer l'invitation
            </Bouton>
            <span className="text-xs text-slate-400">
              {ROLES[form.role].detail} · il choisira son mot de passe depuis le lien reçu
            </span>
          </div>
        </div>
      )}

      {/* Sur téléphone, un tableau de quatre colonnes écrase tout et pousse les
          boutons hors de l'écran : on passe en cartes. */}
      <div className="md:hidden space-y-3">
        {comptes.map(c => (
          <div key={c.identifiant} className={`bg-white border rounded-xl p-4 ${c.actif ? 'border-slate-200' : 'border-slate-200 bg-slate-50/60'}`}>
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <p className={`font-semibold ${c.actif ? 'text-slate-900' : 'text-slate-400'}`}>
                  {c.identifiant}
                  {nomDe(c) && <span className="font-normal text-slate-400 ml-2">{nomDe(c)}</span>}
                </p>
                {(c.email || c.telephone) && (
                  <p className="text-xs text-slate-500 mt-0.5">{[c.email, c.telephone].filter(Boolean).join(' · ')}</p>
                )}
                <p className="text-xs text-slate-400 mt-0.5">Dernier accès : {jour(c.dernierAcces)}</p>
              </div>
              <span className={`shrink-0 rounded-lg border px-2 py-1 text-xs font-semibold ${ROLES[c.role].classe}`}>
                {ROLES[c.role].label}
              </span>
            </div>
            {(!c.actif || c.enAttente) && (
              <div className="flex flex-wrap gap-2 mt-2">
                {!c.actif && <span className="text-xs bg-slate-200 text-slate-500 rounded-full px-2 py-0.5">désactivé</span>}
                {c.enAttente && <span className="text-xs bg-amber-100 text-amber-800 rounded-full px-2 py-0.5">invitation en attente</span>}
              </div>
            )}
            <div className="flex gap-2 mt-3">
              <button onClick={() => ouvrirEdition(c)}
                      className="flex-1 text-xs font-semibold border border-slate-300 hover:border-orange-400 rounded-lg py-2">
                Modifier
              </button>
              <button onClick={() => envoyerLien(c)}
                      className="flex-1 text-xs font-semibold border border-slate-300 hover:border-orange-400 rounded-lg py-2">
                {c.enAttente ? 'Relancer' : 'Réinitialiser'}
              </button>
              <button onClick={() => modifier(c.identifiant, { actif: !c.actif }, c.actif ? `${c.identifiant} désactivé` : `${c.identifiant} réactivé`)}
                      className={`flex-1 text-xs font-semibold border rounded-lg py-2 ${c.actif ? 'border-slate-300 hover:border-red-400 hover:text-red-600' : 'border-emerald-300 text-emerald-700'}`}>
                {c.actif ? 'Désactiver' : 'Réactiver'}
              </button>
            </div>
            {edition === c.identifiant && (
              <div className="mt-3">
                <FormulaireEdition compte={c} brouillon={brouillon} setBrouillon={setBrouillon}
                                   moi={c.identifiant === moi}
                                   onValider={() => enregistrer(c)} onAnnuler={() => setEdition(null)} />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="hidden md:block bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-400">
              <th className="px-4 py-2.5 font-semibold">Compte</th>
              <th className="px-4 py-2.5 font-semibold">Rôle</th>
              <th className="px-4 py-2.5 font-semibold">Dernier accès</th>
              <th className="px-4 py-2.5 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {comptes.map(c => (
              <React.Fragment key={c.identifiant}>
              <tr className={c.actif ? '' : 'bg-slate-50/60'}>
                <td className="px-4 py-3">
                  <span className={`font-semibold ${c.actif ? 'text-slate-900' : 'text-slate-400'}`}>{c.identifiant}</span>
                  {nomDe(c) && <span className="text-slate-400 ml-2">{nomDe(c)}</span>}
                  {!c.actif && <span className="ml-2"><Mention>désactivé</Mention></span>}
                  {c.enAttente && <span className="ml-2"><Mention ton="attente">invitation en attente</Mention></span>}
                  {(c.email || c.telephone) && (
                    <div className="text-xs text-slate-400 mt-0.5">{[c.email, c.telephone].filter(Boolean).join(' · ')}</div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-block rounded-lg border px-2.5 py-1 text-xs font-semibold ${ROLES[c.role].classe}`}>
                    {ROLES[c.role].label}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-400">{jour(c.dernierAcces)}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-2 justify-end">
                    <button onClick={() => (edition === c.identifiant ? setEdition(null) : ouvrirEdition(c))}
                            className="text-xs font-semibold border border-slate-300 hover:border-orange-400 rounded-lg px-3 py-1.5">
                      {edition === c.identifiant ? 'Fermer' : 'Modifier'}
                    </button>
                    <button onClick={() => envoyerLien(c)}
                            className="text-xs font-semibold border border-slate-300 hover:border-orange-400 rounded-lg px-3 py-1.5">
                      {c.enAttente ? 'Relancer l’invitation' : 'Réinitialiser'}
                    </button>
                    <button onClick={() => modifier(c.identifiant, { actif: !c.actif }, c.actif ? `${c.identifiant} désactivé` : `${c.identifiant} réactivé`)}
                            className={`text-xs font-semibold border rounded-lg px-3 py-1.5 ${c.actif ? 'border-slate-300 hover:border-red-400 hover:text-red-600' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'}`}>
                      {c.actif ? 'Désactiver' : 'Réactiver'}
                    </button>
                  </div>
                </td>
              </tr>
              {edition === c.identifiant && (
                <tr className="bg-white">
                  <td colSpan={4} className="px-4 pb-4 pt-0">
                    <FormulaireEdition compte={c} brouillon={brouillon} setBrouillon={setBrouillon}
                                       moi={c.identifiant === moi}
                                       onValider={() => enregistrer(c)} onAnnuler={() => setEdition(null)} />
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-400 mt-3">
        Un mot de passe se choisit depuis un lien reçu par email : personne ici ne le connaît,
        pas même la direction. Un compte désactivé ne peut plus se connecter mais reste sur les
        pièces qu'il a créées, et il doit toujours rester au moins un compte de direction actif.
      </p>

      <Toast message={message} />
    </Page>
  );
}
